// v33h — Rýchle vytvorenie z FAB: Úloha / Pripomienka / Udalosť v JEDNOM poli + rad akčných ikon.
//   • jedno auto-rastúce pole (názov), žiadny nadpis ani popis
//   • pod ním ikony: alarm (pripomienka) · kalendár (udalosť) · opakovanie · kategória · zdieľanie
//   • klik na ikonu rozbalí jej panel; naraz je otvorený len jeden; klik zbalí
//   • nastavená akcia → ikona vo farbe témy
//   • typ sa ODVODÍ: alarm → Pripomienka, kalendár → Udalosť, inak Úloha (alarm/kalendár sa vylučujú)
// Mapovanie na existujúci model: kind=reminder, plan_type note|event (POST /api/tasks).
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { BuzerIcon } from "../../BuzerIcons";
import { OFFSETS } from "../../lib/format";
import { Button, Sheet } from "../ui/ds";
import { DateTimeField, FriendChip, FriendPickerHint, Chk } from "../ui";

const REPEATS = [
  { v: "none", l: "Jednorázovo" }, { v: "daily", l: "Každý deň" }, { v: "weekly", l: "Každý týždeň" },
  { v: "monthly", l: "Každý mesiac" }, { v: "yearly", l: "Každý rok" },
];
const ACTIONS = [
  { key: "calendar", icon: "calendar", label: "Udalosť" },
  { key: "alarm", icon: "alarm", label: "Pripomienka" },
  { key: "recur", icon: "refresh", label: "Opakovanie" },
  { key: "cat", icon: "folder", label: "Kategória" },
  { key: "share", icon: "share", label: "Zdieľať" },
];

export function QuickPlan({ categories = [], onClose, onAdded }) {
  const [label, setLabel] = useState("");
  const [panel, setPanel] = useState(null);     // otvorený panel (len jeden)
  const [due, setDue] = useState("");           // pripomienka: kedy pripomenúť
  const [offs, setOffs] = useState([0]);        // s akým predstihom upozorniť
  const [evStart, setEvStart] = useState("");   // udalosť: začiatok
  const [evEnd, setEvEnd] = useState("");       // udalosť: koniec (voliteľné)
  const [repeat, setRepeat] = useState("none");
  const [category, setCategory] = useState("");
  const [catText, setCatText] = useState("");
  const [shareWith, setShareWith] = useState([]);
  const [friends, setFriends] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const taRef = useRef(null);

  useEffect(() => { api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {}); }, []);
  useEffect(() => { const el = taRef.current; if (el) { el.focus(); grow(el); } }, []);
  function grow(el) { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 148) + "px"; }

  // v33h — udalosť a pripomienka KOEXISTUJÚ: udalosť má dátum, pripomienka je upozornenie.
  // Ak je udalosť → pripomienka = predstih (offsets) pred ňou; inak = samostatný čas (due).
  const isEvent = !!evStart;
  const isReminder = !isEvent && !!due;
  const type = isEvent ? { label: "Udalosť", icon: "calendar" } : isReminder ? { label: "Pripomienka", icon: "alarm" } : { label: "Úloha", icon: "check" };
  const hasReminder = isEvent ? offs.length > 0 : !!due;
  const set = { alarm: hasReminder, calendar: !!evStart, recur: repeat !== "none", cat: !!category, share: shareWith.length > 0 };

  // len prepni panel — nič sa navzájom nemaže (dátum udalosti ani čas pripomienky sa nestratí)
  function openPanel(key) { setPanel((cur) => (cur === key ? null : key)); }
  const toggleOff = (m) => setOffs((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  const toggleShare = (fid) => setShareWith((p) => (p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]));
  const pickCat = (c) => { setCategory((cur) => (cur === c ? "" : c)); setCatText(""); };

  async function submit() {
    const name = label.trim();
    if (!name) { setErr("Napíš, čo treba spraviť."); return; }
    setBusy(true); setErr("");
    const cat = (catText.trim() || category) || "";
    const base = { icon: "", icon_color: null, label: name, desc: "", category: cat, share_with: shareWith };
    let body;
    if (isEvent) {
      // v33h — udalosť dostane pripomienku len ak si zvolíš predstih (inak bez upozornenia)
      body = { ...base, kind: "reminder", plan_type: "event", due_at: evStart.replace("T", " "),
        end_at: evEnd ? evEnd.replace("T", " ") : "", repeat, notify_offsets: offs, notify_push: offs.length > 0, notify_email: false };
    } else if (isReminder) {
      body = { ...base, kind: "reminder", plan_type: "note", due_at: due.replace("T", " "),
        repeat, notify_offsets: offs, notify_push: true, notify_email: false };
    } else {
      body = { ...base, kind: "reminder", plan_type: "note", due_at: "",
        repeat: "none", notify_offsets: [], notify_push: false, notify_email: false };
    }
    try { await api("/api/tasks", { method: "POST", body }); onAdded && onAdded(); onClose && onClose(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <Sheet center title={type.label} icon={type.icon} onClose={onClose}
      footer={<Button variant="primary" block disabled={busy} onClick={submit}>{busy ? "…" : "Pridať"}</Button>}>
      {/* jedno auto-rastúce pole (názov) */}
      <textarea ref={taRef} value={label} rows={2}
        onChange={(e) => { setLabel(e.target.value); grow(e.target); }}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
        placeholder="Čo treba spraviť?"
        style={{ width: "100%", resize: "none", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-lg)",
          background: "var(--surface-solid)", color: "var(--text)", padding: "12px 14px", fontSize: 16, lineHeight: 1.4,
          fontFamily: "var(--ff-ui)", minHeight: 60, outline: "none", WebkitTextFillColor: "currentColor" }} />

      {/* rad akčných ikon */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {ACTIONS.map((a) => {
          const on = set[a.key];
          const open = panel === a.key;
          return (
            <button key={a.key} type="button" onClick={() => openPanel(a.key)} title={a.label} aria-pressed={on}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, cursor: "pointer",
                borderRadius: "50%", WebkitTextFillColor: "currentColor",
                background: on ? "var(--brand-primary)" : open ? "var(--brand-primary-surface-strong)" : "var(--neutral-surface-2)",
                border: open && !on ? "1px solid var(--brand-primary-border)" : "1px solid var(--neutral-border)",
                color: on ? "var(--brand-on-primary)" : "var(--neutral-text-main)", transition: "background .15s, color .15s" }}>
              <BuzerIcon name={a.icon} size={20} color="currentColor" />
            </button>
          );
        })}
      </div>

      {/* rozbalený panel (len jeden naraz) */}
      {panel && (
        <div style={{ marginTop: 12, padding: 14, borderRadius: "var(--radius-lg)", background: "var(--neutral-surface)", border: "1px solid var(--neutral-border)" }}>
          {panel === "alarm" && (<>
            {/* samostatná pripomienka potrebuje vlastný čas; pre udalosť je to predstih pred ňou */}
            {!isEvent && (<>
              <div style={st.fieldHint}>Kedy ťa upozorniť?</div>
              <DateTimeField value={due} onChange={setDue} label="Termín" />
            </>)}
            <div style={{ ...st.fieldHint, marginTop: isEvent ? 0 : 12 }}>{isEvent ? "Pripomenúť pred udalosťou:" : "Upozorniť s predstihom:"}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {/* pri udalosti sa dá zvoliť aj „Žiadna" (bez pripomienky) — vyčistí predstihy */}
              {isEvent && (
                <button type="button" onClick={(e) => { setOffs([]); e.currentTarget.blur(); }} className="chip"
                  style={{ ...st.chip, ...(offs.length === 0 ? st.chipOn : {}) }}>{offs.length === 0 ? <Chk /> : null}Žiadna</button>
              )}
              {OFFSETS.map((o) => {
                const onc = offs.includes(o.m);
                return (
                  <button type="button" key={o.m} onClick={(e) => { toggleOff(o.m); e.currentTarget.blur(); }} className="chip"
                    style={{ ...st.chip, ...(onc ? st.chipOn : {}) }}>{onc ? <Chk /> : null}{o.l}</button>
                );
              })}
            </div>
          </>)}
          {panel === "calendar" && (<>
            <div style={st.fieldHint}>Začiatok udalosti</div>
            <DateTimeField value={evStart} onChange={setEvStart} label="Začiatok" />
            <div style={{ ...st.fieldHint, marginTop: 12 }}>Koniec (voliteľné)</div>
            <DateTimeField value={evEnd} onChange={setEvEnd} label="Koniec" />
            {/* pripomienku k udalosti nastavíš cez ikonu budíka (Pripomienka) */}
          </>)}
          {panel === "recur" && (<>
            <div style={st.fieldHint}>Opakovanie</div>
            {/* v33h — nič nie je predvolené: „Jednorázovo" = žiadny výber; klik na zvolenú ju zruší */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {REPEATS.filter((r) => r.v !== "none").map((r) => (
                <button type="button" key={r.v} onClick={(e) => { setRepeat(repeat === r.v ? "none" : r.v); e.currentTarget.blur(); }} className="chip"
                  style={{ ...st.chip, ...(repeat === r.v ? st.chipOn : {}) }}>{repeat === r.v ? <Chk /> : null}{r.l}</button>
              ))}
            </div>
            <div style={{ ...st.taskDesc, marginTop: 8 }}>Bez výberu = jednorázovo.</div>
          </>)}
          {panel === "cat" && (<>
            <div style={st.fieldHint}>Kategória</div>
            {categories.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {categories.map((c) => (
                  <button type="button" key={c} onClick={(e) => { pickCat(c); e.currentTarget.blur(); }} className="chip"
                    style={{ ...st.chip, ...(category === c && !catText ? st.chipOn : {}) }}>{category === c && !catText ? <Chk /> : null}{c}</button>
                ))}
              </div>
            )}
            <input className="field" value={catText} maxLength={40}
              onChange={(e) => { setCatText(e.target.value); setCategory(""); }}
              placeholder={categories.length ? "alebo napíš novú kategóriu…" : "Napíš kategóriu…"}
              style={{ ...st.field, width: "100%" }} />
          </>)}
          {panel === "share" && (<>
            <div style={st.fieldHint}>Zdieľať s priateľmi — spoločnú položku môžu upravovať obaja.</div>
            {friends.length === 0 ? (
              <div style={st.taskDesc}>Zatiaľ žiadni priatelia — pridaj si ich v záložke Priatelia.</div>
            ) : (<>
              <FriendPickerHint />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {/* v33h — rovnaký friend picker ako pozvánky do výzvy (globálny štýl) */}
                {friends.map((f) => (
                  <FriendChip key={f.friend_id} friend={f} selected={shareWith.includes(f.friend_id)} onToggle={toggleShare}
                    chipStyle={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                      background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }}
                    chipOnStyle={{ background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }} />
                ))}
              </div>
            </>)}
          </>)}
        </div>
      )}
      {err && <div style={{ ...st.err, marginTop: 10 }}>{err}</div>}
    </Sheet>
  );
}
